import { useState } from 'react'
import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, notFound } from '@tanstack/react-router'
import { PlusIcon } from 'lucide-react'

import { Button } from '@ems/ui/components/button'
import { PageHeader } from '@ems/ui/patterns/page-header'
import { StatusBadge } from '@ems/ui/patterns/status-badge'

import { SUPERADMIN_GRANT, type Role } from '#admin/features/access/api.ts'
import { rolesQuery } from '#admin/features/access/queries.ts'
import { RoleEditor } from '#admin/features/access/role-editor.tsx'
import { NotFoundView } from '#admin/components/not-found-view.tsx'
import { sessionQuery } from '#admin/features/auth/queries.ts'
import { can } from '#admin/features/auth/session.ts'
import { RolesSkeleton } from '#admin/features/access/access-skeletons.tsx'

/**
 * `/settings/roles` — what each role in this edition may do.
 *
 * A list and an editor side by side rather than a page per role: an operator deciding what a new
 * "Registration desk" role should hold is usually comparing it with the roles beside it, and a
 * page change between each loses the comparison.
 *
 * Guarded as a 404 without `access.manage`, like `/platform` — the API refuses the list with a
 * 403 anyway, and a page whose first request is refused should not be offered.
 */
export const Route = createFileRoute('/_authenticated/settings/roles')({
  pendingComponent: RolesSkeleton,
  staticData: { crumb: { parents: [{ label: 'Settings' }], label: 'Roles' } },
  loader: async ({ context }) => {
    const session = await context.queryClient.ensureQueryData(sessionQuery())
    if (!can(session, 'access.manage')) throw notFound()
    await context.queryClient.ensureQueryData(rolesQuery())
  },
  // Its own, so the refusal renders in the layout's outlet with the sidebar intact. A thrown
  // not-found goes to the nearest route declaring a component for it, and that route renders it in
  // place of itself — so the layout must not be the one to catch it.
  notFoundComponent: NotFoundView,
  component: RolesPage,
})

function summary(role: Role, moduleCount: number): string {
  if (role.grants.includes(SUPERADMIN_GRANT)) return 'Full control'
  const modules = new Set(role.grants.map((grant) => grant.split('.')[0]))
  if (modules.size === 0) return 'No access'
  return `${modules.size} of ${moduleCount} modules`
}

function RolesPage() {
  const { data } = useSuspenseQuery(rolesQuery())
  // `'new'` while creating; otherwise the selected role, defaulting to the first.
  const [selected, setSelected] = useState<string>(data.items[0]?.id ?? 'new')

  const moduleCount = new Set(
    data.grantable.filter((g) => g !== SUPERADMIN_GRANT).map((g) => g.split('.')[0]),
  ).size
  const role = selected === 'new' ? null : (data.items.find((r) => r.id === selected) ?? null)

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Roles"
        description="What each role may do in this edition. You can hand out only the access you hold yourself."
        actions={
          <Button size="sm" onClick={() => setSelected('new')} disabled={selected === 'new'}>
            <PlusIcon />
            New role
          </Button>
        }
      />

      <div className="grid gap-6 lg:grid-cols-[18rem_1fr]">
        <nav aria-label="Roles">
          <ul className="flex flex-col gap-1">
            {data.items.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  aria-current={item.id === selected ? 'true' : undefined}
                  onClick={() => setSelected(item.id)}
                  className="hover:bg-accent aria-[current=true]:bg-card aria-[current=true]:border-(--border) flex w-full flex-col items-start gap-1 rounded-md border border-transparent px-3 py-2 text-left"
                >
                  <span className="flex w-full items-center gap-2">
                    <span className="truncate font-medium">{item.name}</span>
                    {item.isSystem ? <StatusBadge tone="neutral">system</StatusBadge> : null}
                  </span>
                  <span className="text-muted-foreground text-xs">
                    {summary(item, moduleCount)} · {item.memberCount} member
                    {item.memberCount === 1 ? '' : 's'}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </nav>

        <section
          aria-label={role === null ? 'New role' : `Edit ${role.name}`}
          className="bg-card rounded-lg border border-(--border) p-4 md:p-6"
        >
          <h2 className="mb-4 text-base font-semibold">
            {selected === 'new' ? 'New role' : (role?.name ?? '')}
          </h2>
          {selected !== 'new' && role === null ? (
            // A role just created, before the list has refetched. Rendering the editor here would
            // show a blank "new role" form for the role that was just saved.
            <p className="text-muted-foreground text-sm">Loading&hellip;</p>
          ) : (
            <RoleEditor
              // Keyed on the role so switching roles starts a fresh draft rather than carrying
              // unsaved edits from the last one into the next.
              key={role?.id ?? 'new'}
              role={role}
              roles={data.items}
              grantable={data.grantable}
              onDone={(id) => setSelected(id ?? data.items[0]?.id ?? 'new')}
            />
          )}
        </section>
      </div>
    </div>
  )
}
